import { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { queryKeys } from '../lib/queryKeys.js';
import { CollectionStyles } from '../components/layout/CollectionStyles.jsx';
import { AnnouncementBar } from '../components/layout/AnnouncementBar.jsx';
import { Header } from '../components/layout/Header.jsx';
import { Footer } from '../components/layout/Footer.jsx';
import { MobileNav } from '../components/layout/MobileNav.jsx';
import { WhatsAppButton } from '../components/layout/WhatsAppButton.jsx';
import { SearchOverlay } from '../components/commerce/SearchOverlay.jsx';
import { CartSheet } from '../components/commerce/CartSheet.jsx';
import { FlyToCart } from '../components/commerce/FlyToCart.jsx';
import { Toaster } from '../components/ui/Toast.jsx';
import { Spinner } from '../components/ui/Spinner.jsx';
import { useUi } from '../stores/ui.js';
import { useSession } from '../stores/session.js';
import { Maintenance } from '../pages/Maintenance.jsx';

/**
 * The shell.
 *
 * `data-world` on the wrapper is the whole of the accent mechanism: components
 * reference `--accent`, and this attribute decides what it resolves to. No
 * JavaScript colour logic, no conditional class strings.
 */

const worldFromRoute = (pathname, search) => {
  const explicit = search.get('world');
  if (explicit === 'fragrance' || explicit === 'sport') return explicit;
  return null;
};

export const Layout = () => {
  const location = useLocation();
  const [search] = useSearchParams();

  const world = useUi((state) => state.world);
  const setWorld = useUi((state) => state.setWorld);
  const closeAll = useUi((state) => state.closeAll);
  const signedIn = useSession((state) => state.signedIn);
  const signedOut = useSession((state) => state.signedOut);

  const { data: settings } = useQuery({
    queryKey: queryKeys.settings.public(),
    queryFn: () => api.get('/settings/public'),
    staleTime: 15 * 60 * 1000,
  });

  // Establishes the session once, quietly. A 401 here is the normal case for a
  // first-time visitor and must not surface as an error.
  useQuery({
    queryKey: queryKeys.account.profile(),
    queryFn: async () => {
      try {
        const result = await api.get('/auth/me');
        signedIn(result.customer);
        return result.customer;
      } catch {
        signedOut();
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // A route with an explicit world keeps it; anything else falls back to the
  // neutral shell rather than inheriting the last page's accent.
  useEffect(() => {
    setWorld(worldFromRoute(location.pathname, search));
  }, [location.pathname, search, setWorld]);

  useEffect(() => {
    closeAll();
    // A hash means the router is being asked to reach an anchor, not the top.
    if (!location.hash) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, closeAll, location.hash]);

  if (settings?.maintenance?.enabled) {
    return <Maintenance message={settings.maintenance.message} storeName={settings.store?.name} />;
  }

  return (
    <div data-world={world ?? undefined} className="flex min-h-screen flex-col bg-paper">
      <CollectionStyles />
      <AnnouncementBar />
      <Header />
      <MobileNav />
      <SearchOverlay />
      <CartSheet />

      <main id="main" className="flex-1">
        <Suspense
          fallback={
            <div className="grid min-h-[60vh] place-items-center">
              <Spinner className="size-6 text-muted" label="Loading page" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      <Footer />
      <WhatsAppButton />
      <Toaster />
      <FlyToCart />
    </div>
  );
};

export default Layout;
